import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import supabase from './supabase';
import apiFetch from './api';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

const DEFAULT_ORG_ID = null; // No fallback — new users MUST complete onboarding to get an org

/* ═══════════════════════════════════════════════════════
   ROLE DEFINITIONS & PERMISSIONS
   ═══════════════════════════════════════════════════════ */
export const ROLES = {
  admin: { label: "Admin", description: "Full access to all settings, data, and configuration", level: 0 },
  manager: { label: "Manager", description: "Team oversight, reporting, pipeline management", level: 1 },
  account_exec: { label: "Account Exec", description: "Manage own deals, contacts, and activities", level: 2 },
  sdr: { label: "SDR", description: "Lead generation, prospecting, initial outreach", level: 3 },
  customer_success: { label: "Customer Success", description: "Account health, renewals, and client relationships", level: 2 },
  viewer: { label: "Viewer", description: "Read-only access to dashboards and reports", level: 4 },
};

export const PERMISSIONS = {
  contacts_view: { label: "View Own Contacts", group: "Contacts", desc: "See contacts assigned to you" },
  contacts_view_all: { label: "View All Contacts", group: "Contacts", desc: "See all contacts in the org" },
  contacts_create: { label: "Create Contacts", group: "Contacts", desc: "Add new contacts" },
  contacts_edit: { label: "Edit Contacts", group: "Contacts", desc: "Modify contact records" },
  contacts_delete: { label: "Delete Contacts", group: "Contacts", desc: "Remove contacts permanently" },
  contacts_import: { label: "Import Contacts", group: "Contacts", desc: "Bulk import from CSV/file" },
  contacts_export: { label: "Export Contacts", group: "Contacts", desc: "Export contact data to CSV" },
  contacts_merge: { label: "Merge Contacts", group: "Contacts", desc: "Merge duplicate contacts" },
  contacts_reassign: { label: "Reassign Contacts", group: "Contacts", desc: "Change contact ownership" },
  companies_view: { label: "View Companies", group: "Companies", desc: "See company records" },
  companies_create: { label: "Create Companies", group: "Companies", desc: "Add new companies" },
  companies_edit: { label: "Edit Companies", group: "Companies", desc: "Modify company records" },
  companies_delete: { label: "Delete Companies", group: "Companies", desc: "Remove companies" },
  deals_view: { label: "View Own Deals", group: "Deals", desc: "See deals assigned to you" },
  deals_view_all: { label: "View All Deals", group: "Deals", desc: "See all deals in the org" },
  deals_create: { label: "Create Deals", group: "Deals", desc: "Add new deals" },
  deals_edit: { label: "Edit Deals", group: "Deals", desc: "Modify deal records" },
  deals_delete: { label: "Delete Deals", group: "Deals", desc: "Remove deals permanently" },
  deals_move_stage: { label: "Move Deal Stages", group: "Deals", desc: "Drag deals between stages" },
  deals_reassign: { label: "Reassign Deals", group: "Deals", desc: "Change deal ownership" },
  deals_close: { label: "Close Deals", group: "Deals", desc: "Mark deals as Won or Lost" },
  pipeline_view: { label: "View Pipeline", group: "Pipeline", desc: "Access pipeline board" },
  pipeline_configure: { label: "Configure Stages", group: "Pipeline", desc: "Add/edit/reorder stages" },
  pipeline_create_custom: { label: "Create Custom Pipelines", group: "Pipeline", desc: "Build additional pipelines" },
  pipeline_forecasting: { label: "View Forecasting", group: "Pipeline", desc: "Access forecast data" },
  email_send: { label: "Send Emails", group: "Email & SMS", desc: "Send emails from CRM" },
  email_view_all: { label: "View All Email", group: "Email & SMS", desc: "See email across the org" },
  email_templates: { label: "Manage Templates", group: "Email & SMS", desc: "Create/edit email templates" },
  email_bulk_send: { label: "Bulk Email Send", group: "Email & SMS", desc: "Send mass emails" },
  email_signatures: { label: "Manage Signatures", group: "Email & SMS", desc: "Create/edit signatures" },
  emails_delete: { label: "Delete Emails", group: "Email & SMS", desc: "Move emails to trash and permanently delete" },
  email_access_manage: { label: "Manage Email Access", group: "Email & SMS", desc: "Grant or revoke access to view other users' connected inboxes" },
  sms_send: { label: "Send SMS", group: "Email & SMS", desc: "Send text messages" },
  sms_view_all: { label: "View All SMS", group: "Email & SMS", desc: "See all SMS conversations" },
  sms_bulk_send: { label: "Bulk SMS Send", group: "Email & SMS", desc: "Send mass text campaigns" },
  dialer_use: { label: "Use Dialer", group: "Dialer & Calls", desc: "Make calls via power dialer" },
  dialer_view_logs: { label: "View Call Logs", group: "Dialer & Calls", desc: "Access call history" },
  dialer_view_all_logs: { label: "View All Call Logs", group: "Dialer & Calls", desc: "See all users call logs" },
  dialer_recordings: { label: "Access Recordings", group: "Dialer & Calls", desc: "Listen to call recordings" },
  dialer_delete_recordings: { label: "Delete Recordings", group: "Dialer & Calls", desc: "Remove call recordings" },
  dialer_manage_queue: { label: "Manage Call Queue", group: "Dialer & Calls", desc: "Add/remove from queue" },
  tasks_view: { label: "View Own Tasks", group: "Tasks", desc: "See tasks assigned to you" },
  tasks_view_all: { label: "View All Tasks", group: "Tasks", desc: "See all tasks in the org" },
  tasks_create: { label: "Create Tasks", group: "Tasks", desc: "Add new tasks" },
  tasks_edit: { label: "Edit Tasks", group: "Tasks", desc: "Modify task records" },
  tasks_assign: { label: "Assign Tasks", group: "Tasks", desc: "Assign tasks to others" },
  tasks_delete: { label: "Delete Tasks", group: "Tasks", desc: "Remove tasks" },
  calendar_view: { label: "View Calendar", group: "Calendar", desc: "See your calendar" },
  calendar_view_team: { label: "View Team Calendar", group: "Calendar", desc: "See all team calendars" },
  calendar_create_events: { label: "Create Events", group: "Calendar", desc: "Add meetings and events" },
  calendar_manage_booking: { label: "Manage Booking Pages", group: "Calendar", desc: "Create/edit booking links" },
  projects_view: { label: "View Projects", group: "Projects", desc: "See project boards" },
  projects_create: { label: "Create Projects", group: "Projects", desc: "Create new projects" },
  projects_edit: { label: "Edit Projects", group: "Projects", desc: "Modify project settings" },
  projects_delete: { label: "Delete Projects", group: "Projects", desc: "Remove projects" },
  lists_view: { label: "View Lists", group: "Lists", desc: "See saved lists and segments" },
  lists_create: { label: "Create Lists", group: "Lists", desc: "Build new lists and segments" },
  lists_edit: { label: "Edit Lists", group: "Lists", desc: "Modify list criteria" },
  lists_delete: { label: "Delete Lists", group: "Lists", desc: "Remove lists" },
  campaigns_view: { label: "View Campaigns", group: "Campaigns", desc: "See campaign dashboard" },
  campaigns_create: { label: "Create Campaigns", group: "Campaigns", desc: "Set up outreach campaigns" },
  campaigns_manage: { label: "Manage Campaigns", group: "Campaigns", desc: "Edit/pause/delete campaigns" },
  campaigns_send: { label: "Send Campaigns", group: "Campaigns", desc: "Execute and launch campaigns" },
  workflows_view: { label: "View Workflows", group: "Automation", desc: "See automation workflows" },
  workflows_create: { label: "Create Workflows", group: "Automation", desc: "Build new automations" },
  workflows_edit: { label: "Edit Workflows", group: "Automation", desc: "Modify existing workflows" },
  workflows_delete: { label: "Delete Workflows", group: "Automation", desc: "Remove workflows" },
  workflows_activate: { label: "Activate Workflows", group: "Automation", desc: "Turn workflows on/off" },
  analytics_view: { label: "View Analytics", group: "Analytics", desc: "Access reports and dashboards" },
  analytics_view_team: { label: "View Team Analytics", group: "Analytics", desc: "See team-wide performance" },
  analytics_export: { label: "Export Reports", group: "Analytics", desc: "Export reports to CSV/PDF" },
  analytics_custom: { label: "Create Custom Reports", group: "Analytics", desc: "Build custom report views" },
  ai_agents: { label: "Use AI Agents", group: "AI", desc: "Access and run AI agents" },
  ai_voice: { label: "Voice Commands", group: "AI", desc: "Use voice command system" },
  ai_core_chat: { label: "Core Chat (AI)", group: "AI", desc: "Use the AI chat assistant" },
  ai_configure: { label: "Configure AI Agents", group: "AI", desc: "Change agent settings in admin" },
  team_chat: { label: "Use Team Chat", group: "Team Chat", desc: "Access internal messaging" },
  team_chat_create_channel: { label: "Create Channels", group: "Team Chat", desc: "Create new chat channels" },
  team_chat_manage_channels: { label: "Manage Channels", group: "Team Chat", desc: "Edit/delete channels" },
  team_chat_dm: { label: "Direct Messages", group: "Team Chat", desc: "Send direct messages" },
  admin_settings: { label: "Admin Settings", group: "Admin", desc: "Access admin center" },
  admin_team: { label: "Manage Team", group: "Admin", desc: "Invite/remove users, change roles" },
  admin_permissions: { label: "Manage Permissions", group: "Admin", desc: "Edit user permissions" },
  admin_billing: { label: "Manage Billing", group: "Admin", desc: "View/change subscription and billing" },
  admin_integrations: { label: "Manage Integrations", group: "Admin", desc: "Connect/disconnect integrations" },
  admin_phone_numbers: { label: "Manage Phone Numbers", group: "Admin", desc: "Buy/release numbers, assign caller ID" },
  admin_custom_fields: { label: "Manage Custom Fields", group: "Admin", desc: "Add/edit custom fields" },
  admin_pipeline_config: { label: "Configure Pipeline", group: "Admin", desc: "Edit pipeline stages" },
  admin_lead_scoring: { label: "Configure Lead Scoring", group: "Admin", desc: "Set scoring rules and weights" },
  admin_quotas: { label: "Set Quotas & KPIs", group: "Admin", desc: "Configure team targets" },
};

export const ROLE_PERMISSIONS = {
  admin: Object.keys(PERMISSIONS),
  manager: ["contacts_view","contacts_view_all","contacts_create","contacts_edit","contacts_delete","contacts_import","contacts_export","contacts_merge","contacts_reassign","companies_view","companies_create","companies_edit","companies_delete","deals_view","deals_view_all","deals_create","deals_edit","deals_delete","deals_move_stage","deals_reassign","deals_close","pipeline_view","pipeline_forecasting","email_send","email_view_all","email_templates","email_bulk_send","email_signatures","emails_delete","sms_send","sms_view_all","sms_bulk_send","dialer_use","dialer_view_logs","dialer_view_all_logs","dialer_recordings","dialer_manage_queue","tasks_view","tasks_view_all","tasks_create","tasks_edit","tasks_assign","tasks_delete","calendar_view","calendar_view_team","calendar_create_events","calendar_manage_booking","projects_view","projects_create","projects_edit","lists_view","lists_create","lists_edit","lists_delete","campaigns_view","campaigns_create","campaigns_manage","campaigns_send","workflows_view","workflows_create","workflows_edit","workflows_activate","analytics_view","analytics_view_team","analytics_export","analytics_custom","ai_agents","ai_voice","ai_core_chat","team_chat","team_chat_create_channel","team_chat_dm","admin_settings","admin_team","admin_quotas"],
  account_exec: ["contacts_view","contacts_create","contacts_edit","contacts_export","companies_view","companies_create","companies_edit","companies_delete","deals_view","deals_create","deals_edit","deals_move_stage","deals_close","pipeline_view","email_send","email_templates","email_signatures","emails_delete","sms_send","dialer_use","dialer_view_logs","dialer_recordings","dialer_manage_queue","tasks_view","tasks_create","tasks_edit","calendar_view","calendar_create_events","calendar_manage_booking","projects_view","projects_create","lists_view","lists_create","campaigns_view","campaigns_create","workflows_view","analytics_view","ai_agents","ai_voice","ai_core_chat","team_chat","team_chat_dm"],
  sdr: ["contacts_view","contacts_create","contacts_edit","companies_view","companies_create","companies_delete","deals_view","deals_create","deals_edit","deals_move_stage","pipeline_view","email_send","emails_delete","sms_send","dialer_use","dialer_view_logs","dialer_manage_queue","tasks_view","tasks_create","calendar_view","calendar_create_events","lists_view","campaigns_view","campaigns_create","analytics_view","ai_agents","ai_voice","ai_core_chat","team_chat","team_chat_dm"],
  customer_success: ["contacts_view","contacts_view_all","contacts_create","contacts_edit","companies_view","companies_create","companies_edit","companies_delete","deals_view","deals_view_all","deals_create","deals_edit","deals_close","pipeline_view","email_send","email_templates","email_signatures","emails_delete","sms_send","dialer_use","dialer_view_logs","dialer_recordings","tasks_view","tasks_view_all","tasks_create","tasks_edit","tasks_assign","calendar_view","calendar_view_team","calendar_create_events","projects_view","projects_create","projects_edit","lists_view","lists_create","analytics_view","analytics_view_team","ai_agents","ai_voice","ai_core_chat","team_chat","team_chat_dm"],
  viewer: ["contacts_view","companies_view","deals_view","pipeline_view","tasks_view","calendar_view","projects_view","lists_view","analytics_view","team_chat","team_chat_dm"],
};

/* ═══════════════════════════════════════════════════════
   HELPER: Supabase query with timeout
   ═══════════════════════════════════════════════════════ */
function withTimeout(promise, ms = 12000) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("Query timeout")), ms)),
  ]);
}

/* ═══════════════════════════════════════════════════════
   BOOTSTRAP ERROR CLASSIFICATION + RETRY
   ═══════════════════════════════════════════════════════ */

// Tri-state outcome for a lookup:
//   { ok: true,  row }              → success; row may be null (confirmed empty)
//   { ok: false, transient: true }  → network / timeout / 5xx → retry candidate
//   { ok: false, transient: false } → permanent (4xx etc.)
const classify = (data, error) =>
  error ? { ok: false, transient: true, error } : { ok: true, row: data || null };

// Exponential-backoff retry around a thunk returning an ok/transient object.
// Attempts default: 3 (500ms, 1000ms, 2000ms waits between). Non-transient and
// successful outcomes return immediately.
async function withRetry(fn, { attempts = 3, baseMs = 500 } = {}) {
  let last;
  for (let i = 0; i < attempts; i++) {
    try {
      last = await fn();
      if (!last || last.ok !== false || !last.transient) return last;
    } catch (e) {
      last = { ok: false, transient: true, error: e };
    }
    if (i < attempts - 1) await new Promise(r => setTimeout(r, baseMs * Math.pow(2, i)));
  }
  return last;
}

// HTTP wrapper: 2xx→ok; 4xx→permanent; 5xx or thrown→transient.
async function safeFetch(url, opts) {
  try {
    const resp = await withTimeout(fetch(url, opts));
    const body = await resp.json().catch(() => null);
    if (resp.ok) return { ok: true, body };
    if (resp.status >= 500) return { ok: false, transient: true, status: resp.status, body };
    return { ok: false, transient: false, status: resp.status, body };
  } catch (e) {
    return { ok: false, transient: true, error: e };
  }
}

/* ═══════════════════════════════════════════════════════
   AUTH PROVIDER
   ═══════════════════════════════════════════════════════ */
export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [bootstrapFailed, setBootstrapFailed] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [pendingInvite, setPendingInvite] = useState(null);
  // Mirror of currentUser for consumption by the onAuthStateChange closure
  // (subscription is created once at mount; its closure captures currentUser=null).
  const currentUserRef = useRef(null);

  // ── Build user from auth + optional DB profile ──
  function buildUser(authUser, dbProfile, isSignedInUser = false) {
    if (isSignedInUser) {
      window.__coreflow_auth_email = authUser?.email || "";
    }
    if (dbProfile) {
      if (isSignedInUser) {
        window.__coreflow_org_id = dbProfile.org_id || DEFAULT_ORG_ID;
        window.__coreflow_user_id = dbProfile.id;
      }
      return {
        id: dbProfile.id,
        firstName: dbProfile.first_name,
        lastName: dbProfile.last_name,
        email: dbProfile.email,
        role: dbProfile.role || "admin",
        avatar: dbProfile.avatar || (dbProfile.first_name?.[0] || "") + (dbProfile.last_name?.[0] || ""),
        avatarUrl: dbProfile.avatar_url || null,
        phone: dbProfile.phone || "",
        title: dbProfile.title || "",
        status: dbProfile.is_active ? "active" : "suspended",
        quota: Number(dbProfile.quota) || 0,
        permissions: dbProfile.permissions || null,
        lastLogin: dbProfile.last_login,
        createdAt: dbProfile.created_at,
        _dbId: dbProfile.id,
        _orgId: dbProfile.org_id,
        _authId: dbProfile.auth_id || authUser.id,
        // Only populate _org when the org-billing fetch succeeded. Absent means
        // "unknown — transient error" and App.js will treat as allowed.
        ...(dbProfile._org_loaded ? {
          _org: {
            id: dbProfile.org_id,
            plan: dbProfile._org_plan,
            plan_status: dbProfile._org_plan_status,
            stripe_subscription_id: dbProfile._org_stripe_sub,
            billing_exempt: dbProfile._org_billing_exempt,
          },
        } : {}),
      };
    }
    const name = authUser.user_metadata?.full_name || authUser.email.split("@")[0];
    const parts = name.split(" ");
    return {
      id: authUser.id,
      firstName: parts[0] || authUser.email.split("@")[0],
      lastName: parts.slice(1).join(" ") || "",
      email: authUser.email,
      role: "admin",
      avatar: (parts[0]?.[0] || "").toUpperCase() + (parts[1]?.[0] || "").toUpperCase(),
      status: "active",
      quota: 0,
      permissions: null,
      lastLogin: new Date().toISOString(),
      _dbId: authUser.id,
      _orgId: DEFAULT_ORG_ID,
      _authId: authUser.id,
    };
  }

  // ── Load user profile (with invite + new org detection) ──
  // Each step distinguishes (b) confirmed-empty from (c) transient error and
  // retries transient errors with exponential backoff. Onboarding is only
  // entered if every step confirmed empty — any transient-after-retries
  // surfaces bootstrapFailed for the shell to render a "Reconnecting…" state.
  const loadUserProfile = useCallback(async (authUser) => {
    if (!authUser) { setCurrentUser(null); return null; }
    console.log("[CoreFlow Auth] Loading profile for:", authUser.email);

    let anyTransient = false;

    // Run a Supabase query through withTimeout + classify. Wraps thrown errors as transient.
    const runQuery = async (queryBuilder) => {
      try {
        const { data, error } = await withTimeout(queryBuilder);
        return classify(data, error);
      } catch (e) {
        return { ok: false, transient: true, error: e };
      }
    };

    // Fetch org billing columns and merge onto a user row for buildUser consumption.
    // Fail-open on transient errors: return the userRow WITHOUT _org_* fields so
    // buildUser leaves _org undefined, and the App.js gate treats undefined as
    // "don't know yet — let them through." A Supabase outage must not eject paid
    // users. Only an explicit empty/null org row (the query succeeded, no sub
    // present) blocks access.
    const mergeOrgBilling = async (userRow) => {
      if (!userRow?.org_id) return userRow;
      try {
        const { data: org, error } = await withTimeout(
          supabase.from("organizations")
            .select("plan, plan_status, stripe_subscription_id, billing_exempt")
            .eq("id", userRow.org_id).maybeSingle()
        );
        if (error) {
          console.warn("[CoreFlow Auth] mergeOrgBilling transient error:", error.message);
          return { ...userRow };
        }
        // _org_loaded is the success sentinel buildUser reads to decide whether
        // to populate currentUser._org. Absent → App.js treats as "don't know."
        return { ...userRow,
          _org_loaded: true,
          _org_plan: org?.plan ?? null,
          _org_plan_status: org?.plan_status ?? null,
          _org_stripe_sub: org?.stripe_subscription_id ?? null,
          _org_billing_exempt: org?.billing_exempt ?? false,
        };
      } catch (e) {
        console.warn("[CoreFlow Auth] mergeOrgBilling threw:", e.message);
        return { ...userRow };
      }
    };

    // 1. Try auth_id lookup (with retry)
    const s1 = await withRetry(() =>
      runQuery(supabase.from("users").select("*").eq("auth_id", authUser.id).maybeSingle())
    );
    if (s1?.ok && s1.row) {
      console.log("[CoreFlow Auth] Found by auth_id");
      const merged = await mergeOrgBilling(s1.row);
      const user = buildUser(authUser, merged, true);
      setCurrentUser(user);
      setBootstrapFailed(false);
      await checkOnboarding(s1.row.org_id);
      return user;
    }
    if (s1 && s1.ok === false && s1.transient) {
      console.warn("[CoreFlow Auth] auth_id lookup transient after retries");
      anyTransient = true;
    }

    // 2. Try email lookup (with retry)
    const s2 = await withRetry(() =>
      runQuery(supabase.from("users").select("*").eq("email", authUser.email).maybeSingle())
    );
    if (s2?.ok && s2.row) {
      console.log("[CoreFlow Auth] Found by email, linking auth_id via server");
      // Best-effort link — don't fail bootstrap on link error
      await withRetry(() => safeFetch("/api/auth/link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: authUser.email, authId: authUser.id }),
      }));
      const merged = await mergeOrgBilling(s2.row);
      const user = buildUser(authUser, merged, true);
      setCurrentUser(user);
      setBootstrapFailed(false);
      await checkOnboarding(s2.row.org_id);
      return user;
    }
    if (s2 && s2.ok === false && s2.transient) {
      console.warn("[CoreFlow Auth] email lookup transient after retries");
      anyTransient = true;
    }

    // 3. Client lookups returned empty — try server-side link (RLS bypass)
    console.log("[CoreFlow Auth] Client lookups failed. Trying server-side link...");
    const s3link = await withRetry(() => safeFetch("/api/auth/link", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: authUser.email, authId: authUser.id }),
    }));
    if (s3link?.ok && s3link.body?.success && s3link.body?.orgId) {
      console.log("[CoreFlow Auth] Server linked auth_id, reloading profile...");
      const s3reload = await withRetry(() =>
        runQuery(supabase.from("users").select("*").eq("auth_id", authUser.id).maybeSingle())
      );
      if (s3reload?.ok && s3reload.row) {
        console.log("[CoreFlow Auth] Profile loaded after server link");
        const merged = await mergeOrgBilling(s3reload.row);
        const user = buildUser(authUser, merged, true);
        setCurrentUser(user);
        setBootstrapFailed(false);
        await checkOnboarding(s3reload.row.org_id);
        return user;
      }
      if (s3reload && s3reload.ok === false && s3reload.transient) anyTransient = true;
    }
    if (s3link && s3link.ok === false && s3link.transient) anyTransient = true;

    // 5. No user record found — check for pending invite
    console.log("[CoreFlow Auth] No DB profile found. Checking for invite...");

    // Check URL for invite token, then localStorage fallback
    const hash = window.location.hash;
    let inviteToken = null;
    const inviteMatch = hash.match(/#invite\/([a-f0-9]+)/);
    if (inviteMatch) {
      inviteToken = inviteMatch[1];
    } else {
      inviteToken = localStorage.getItem("coreflow_invite_token");
      if (inviteToken) console.log("[CoreFlow Auth] Recovered invite token from localStorage");
    }

    if (inviteToken) {
      const validate = await withRetry(() => safeFetch(`/api/invite/validate?token=${inviteToken}`));
      if (validate?.ok && validate.body?.valid) {
        console.log("[CoreFlow Auth] Valid invite found, accepting...");
        const firstName = validate.body.invite?.first_name || authUser.user_metadata?.full_name?.split(" ")[0] || authUser.email.split("@")[0];
        const lastName = validate.body.invite?.last_name || authUser.user_metadata?.full_name?.split(" ").slice(1).join(" ") || "";
        const accept = await withRetry(() => safeFetch("/api/invite/accept", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token: inviteToken,
            authUserId: authUser.id,
            firstName,
            lastName,
          }),
        }));
        if (accept?.ok && accept.body?.success) {
          localStorage.removeItem("coreflow_invite_token");
          window.history.pushState({}, "", "/dashboard");
          return loadUserProfile(authUser);
        }
        if (accept && accept.ok === false && accept.transient) anyTransient = true;
      } else if (validate && validate.ok === false && validate.transient) {
        anyTransient = true;
      }
      // Clean up token regardless
      localStorage.removeItem("coreflow_invite_token");
    }

    // 6. Check if there's a pending invite in the DB for this email
    console.log("[CoreFlow Auth] Checking invites DB for:", authUser.email.toLowerCase());
    const s6 = await withRetry(() =>
      runQuery(
        supabase.from("invites")
          .select("*, organizations(name)")
          .eq("email", authUser.email.toLowerCase())
          .eq("status", "pending")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle()
      )
    );
    if (s6?.ok && s6.row) {
      const invite = s6.row;
      console.log("[CoreFlow Auth] Found pending invite for:", authUser.email, "→ org:", invite.org_id);
      const firstName = invite.first_name || authUser.user_metadata?.full_name?.split(" ")[0] || authUser.email.split("@")[0];
      const lastName = invite.last_name || authUser.user_metadata?.full_name?.split(" ").slice(1).join(" ") || "";
      const accept = await withRetry(() => safeFetch("/api/invite/accept", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: invite.token,
          authUserId: authUser.id,
          firstName,
          lastName,
        }),
      }));
      if (accept?.ok && accept.body?.success) {
        window.history.pushState({}, "", "/dashboard");
        return loadUserProfile(authUser);
      }
      if (accept && accept.ok === false && accept.transient) anyTransient = true;
    } else if (s6 && s6.ok === false && s6.transient) {
      anyTransient = true;
    } else {
      console.log("[CoreFlow Auth] No pending invite found in DB for:", authUser.email);
    }

    // 6b. Last resort — check if there's ANY invite (even accepted) and a user record was created
    const s6b = await withRetry(() =>
      runQuery(
        supabase.from("invites")
          .select("org_id")
          .eq("email", authUser.email.toLowerCase())
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle()
      )
    );
    if (s6b?.ok && s6b.row) {
      console.log("[CoreFlow Auth] Found historical invite, checking for user record in org:", s6b.row.org_id);
      await withRetry(() => safeFetch("/api/auth/link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authId: authUser.id, email: authUser.email }),
      }));
      const profile = await loadUserProfile(authUser);
      if (profile && profile.orgId) {
        window.history.pushState({}, "", "/dashboard");
        return profile;
      }
    } else if (s6b && s6b.ok === false && s6b.transient) {
      anyTransient = true;
    }

    // 7. Transient-gate: enter onboarding only on CONFIRMED empty across all steps
    if (anyTransient) {
      console.warn("[CoreFlow Auth] Bootstrap hit transient errors after retries — NOT entering onboarding. Will retry on next auth event.");
      setBootstrapFailed(true);
      return buildUser(authUser, null, true);
    }

    // 8. Block OAuth signups — Google OAuth is login-only for existing users.
    // A brand-new user authenticating via OAuth has no Stripe context and would
    // slip through to onboarding → free org. Sign them out and push to /signup.
    const provider = authUser.app_metadata?.provider;
    if (provider && provider !== "email") {
      console.warn("[CoreFlow Auth] OAuth signup blocked for", authUser.email, "(provider:", provider, ")");
      await supabase.auth.signOut();
      if (typeof window !== "undefined") {
        window.location.href = "/signup?reason=oauth_signup_blocked";
      }
      return null;
    }

    // 9. No invite — this is a new email/password user. They need to create an org.
    console.log("[CoreFlow Auth] New user, needs onboarding");
    const sessionUser = buildUser(authUser, null, true);
    setCurrentUser(sessionUser);
    setBootstrapFailed(false);
    setNeedsOnboarding(true);
    setOnboardingStep(0);
    return sessionUser;
  }, []);

  // ── Check if org still needs onboarding ──
  async function checkOnboarding(orgId) {
    if (!orgId) {
      // No org — user must go through onboarding
      setNeedsOnboarding(true);
      return;
    }
    try {
      const { data: org } = await supabase.from("organizations")
        .select("onboarding_complete, onboarding_step")
        .eq("id", orgId)
        .maybeSingle();
      if (org && !org.onboarding_complete) {
        setNeedsOnboarding(true);
        setOnboardingStep(org.onboarding_step || 1);
      } else {
        setNeedsOnboarding(false);
      }
    } catch (e) {
      setNeedsOnboarding(false);
    }
  }

  // ── Create org for new user ──
  const createOrg = useCallback(async ({ orgName, companyDomain, firstName, lastName }) => {
    if (!session?.user) return { success: false, error: "Not authenticated" };
    try {
      // Read Stripe plan data saved during checkout flow
      let planData = {};
      try {
        planData = JSON.parse(localStorage.getItem("coreflow_plan") || "{}");
      } catch (_) {}

      const resp = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          authUserId: session.user.id,
          email: session.user.email,
          firstName: firstName || session.user.user_metadata?.full_name?.split(" ")[0] || "",
          lastName: lastName || session.user.user_metadata?.full_name?.split(" ").slice(1).join(" ") || "",
          orgName,
          companyDomain,
          plan: planData.plan || null,
          seats: planData.seats || null,
          stripeCustomerId: planData.stripeCustomerId || null,
          stripeSubscriptionId: planData.stripeSubscriptionId || null,
          stripe_session_id: planData.stripeSessionId || null,
        }),
      });
      const data = await resp.json();
      if (data.success) {
        localStorage.removeItem("coreflow_plan");
        setOnboardingStep(1);
        // Reload user profile to pick up the new org
        await loadUserProfile(session.user);
        return { success: true, orgId: data.orgId };
      }
      return { success: false, error: data.error };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }, [session, loadUserProfile]);

  // ── Complete onboarding ──
  const completeOnboarding = useCallback(async () => {
    const orgId = currentUser?._orgId;
    if (!orgId) return;
    await supabase.from("organizations").update({
      onboarding_complete: true,
      onboarding_step: 99,
    }).eq("id", orgId);
    setNeedsOnboarding(false);
    setOnboardingStep(99);
  }, [currentUser]);

  // ── Update onboarding step ──
  const updateOnboardingStep = useCallback(async (step) => {
    const orgId = currentUser?._orgId;
    if (!orgId) return;
    await supabase.from("organizations").update({ onboarding_step: step }).eq("id", orgId);
    setOnboardingStep(step);
  }, [currentUser]);

  // ── Load org users ──
  const loadOrgUsers = useCallback(async () => {
    if (!currentUser?._orgId) return;
    try {
      const { data } = await withTimeout(
        supabase.from("users").select("*").eq("org_id", currentUser._orgId), 5000
      );
      if (data) setUsers(data.map(u => buildUser({ id: u.auth_id, email: u.email, user_metadata: {} }, u)));
    } catch (e) {
      console.warn("[CoreFlow Auth] Org users load failed:", e.message);
    }
  }, [currentUser?._orgId]);

  useEffect(() => { loadOrgUsers(); }, [loadOrgUsers]);

  // Keep currentUserRef in sync so the onAuthStateChange closure reads latest
  useEffect(() => { currentUserRef.current = currentUser; }, [currentUser]);

  // ── Initialize ──
  useEffect(() => {
    let mounted = true;

    async function init() {
      try {
        console.log("[CoreFlow Auth] Checking session...");
        const { data: { session: existing } } = await supabase.auth.getSession();
        if (mounted && existing?.user) {
          console.log("[CoreFlow Auth] Found existing session");
          setSession(existing);
          const sessionUser = buildUser(existing.user, null, true);
          setCurrentUser(sessionUser);
          setAuthLoading(false);
          loadUserProfile(existing.user);
        } else {
          console.log("[CoreFlow Auth] No existing session");
          if (mounted) setAuthLoading(false);
        }
      } catch (err) {
        console.warn("[CoreFlow Auth] Init error:", err.message);
        if (mounted) setAuthLoading(false);
      }
    }

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (!mounted) return;
      console.log("[CoreFlow Auth] Auth event:", event);
      setSession(newSession);

      // Password recovery — don't trigger onboarding, redirect to reset page
      if (event === "PASSWORD_RECOVERY") {
        console.log("[CoreFlow Auth] Password recovery detected — redirecting");
        setIsPasswordRecovery(true);
        setAuthLoading(false);
        if (newSession?.user) {
          const sessionUser = buildUser(newSession.user, null, true);
          setCurrentUser(sessionUser);
        }
        window.history.pushState({}, "", "/reset-password");
        return;
      }

      if (event === "SIGNED_IN" && newSession?.user) {
        // Skip profile loading if this is a password recovery flow
        if (isPasswordRecovery) return;
        // Only load profile once per session — prevent re-render loops.
        // Read from ref, not closure-captured state (subscription is created
        // once at mount; closure-captured currentUser is frozen at null).
        if (currentUserRef.current?._authId === newSession.user.id) {
          console.log("[CoreFlow Auth] Already loaded, skipping duplicate SIGNED_IN");
          return;
        }
        const sessionUser = buildUser(newSession.user, null, true);
        setCurrentUser(sessionUser);
        setAuthLoading(false);
        loadUserProfile(newSession.user);
      } else if (event === "SIGNED_OUT") {
        setCurrentUser(null);
        setUsers([]);
        setNeedsOnboarding(false);
        setBootstrapFailed(false);
        setIsPasswordRecovery(false);
      }
    });

    return () => { mounted = false; subscription.unsubscribe(); };
  }, [loadUserProfile]);

  // ═══════════════════════════════════════════
  // AUTH ACTIONS
  // ═══════════════════════════════════════════
  const login = useCallback(async (email, password) => {
    setAuthError(null);
    console.log("[CoreFlow Auth] Signing in...");
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        console.log("[CoreFlow Auth] Error:", error.message);
        const msg = error.message?.toLowerCase() || "";
        if (msg.includes("rate limit") || error.status === 429) {
          return { success: false, error: "Too many sign-in attempts. Please wait a moment and try again, or use Google sign-in.", rateLimited: true };
        }
        return { success: false, error: error.message };
      }
      console.log("[CoreFlow Auth] Sign-in success");
      return { success: true, user: data.user };
    } catch (e) {
      if (e?.status === 429 || e?.message?.includes("429")) {
        return { success: false, error: "Too many sign-in attempts. Please wait a moment and try again, or use Google sign-in.", rateLimited: true };
      }
      return { success: false, error: e.message || "Sign-in failed. Please try again." };
    }
  }, []);

  const signup = useCallback(async (email, password, fullName) => {
    setAuthError(null);
    try {
      const { data, error } = await supabase.auth.signUp({
        email, password,
        options: { data: { full_name: fullName } },
      });
      if (error) {
        const msg = error.message?.toLowerCase() || "";
        if (msg.includes("rate limit") || error.status === 429) {
          return { success: false, error: "Too many attempts. Please wait a moment and try again, or use Google sign-in instead.", rateLimited: true };
        }
        if (msg.includes("already registered") || msg.includes("already been registered")) {
          return { success: false, error: "This email is already registered. Try signing in instead.", alreadyExists: true };
        }
        return { success: false, error: error.message };
      }
      return { success: true, user: data.user, needsConfirmation: !data.session };
    } catch (e) {
      if (e?.status === 429 || e?.message?.includes("429")) {
        return { success: false, error: "Too many attempts. Please wait a moment and try again, or use Google sign-in instead.", rateLimited: true };
      }
      return { success: false, error: e.message || "Signup failed. Please try again." };
    }
  }, []);

  const loginWithGoogle = useCallback(async () => {
    setAuthError(null);
    // Preserve invite token in redirect AND localStorage
    const hash = window.location.hash;
    const inviteMatch = hash.match(/#invite\/([a-f0-9]+)/);
    if (inviteMatch) {
      localStorage.setItem("coreflow_invite_token", inviteMatch[1]);
    }
    const redirectTo = hash.includes("invite/")
      ? window.location.origin + "/" + hash
      : window.location.origin;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  }, []);

  const loginWithMagicLink = useCallback(async (email) => {
    setAuthError(null);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setCurrentUser(null); setSession(null); setUsers([]);
    setNeedsOnboarding(false);
  }, []);

  const resetPassword = useCallback(async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + "/reset-password",
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  }, []);

  // ═══════════════════════════════════════════
  // USER MANAGEMENT
  // ═══════════════════════════════════════════
  const inviteUser = useCallback(async ({ firstName, lastName, email, role, title, quota }) => {
    if (users.find(u => u.email.toLowerCase() === email.toLowerCase())) {
      return { success: false, error: "A user with this email already exists" };
    }
    try {
      // Create invite via API (sends email)
      const resp = await fetch("/api/invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orgId: currentUser?._orgId,
          email: email.toLowerCase(),
          role: role || "sdr",
          invitedBy: currentUser?.id,
          firstName: firstName || "",
          lastName: lastName || "",
        }),
      });
      const data = await resp.json();
      if (!resp.ok) return { success: false, error: data.error };
      return { success: true, inviteUrl: data.inviteUrl, invite: data.invite };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }, [users, currentUser]);

  // ═══════════════════════════════════════════════════════════════════
  // ADMIN USER MANAGEMENT
  //
  // Migration 046 removes the permissive `users_org_access` FOR ALL RLS
  // policy on the `users` table. After 046, anon-client UPDATE/DELETE
  // on `users` only succeeds for the caller's OWN row (`auth_id =
  // auth.uid()` self-update policy). All cross-user admin actions go
  // through `POST /api/admin/users` which uses service-role + the three
  // CHECKs (org match, self-block, role hierarchy) defined in
  // api/admin/users.js.
  //
  // Function names are unchanged so the rest of the codebase is
  // unaffected. Each wrapper still does the optimistic local-state
  // update + rollback on API failure to preserve the existing UX.
  // ═══════════════════════════════════════════════════════════════════
  const callAdminUsers = async (body) => {
    try {
      const resp = await apiFetch("/api/admin/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) return { success: false, error: data.error || ("HTTP " + resp.status) };
      return { success: true, user: data.user };
    } catch (e) {
      return { success: false, error: e.message || "Network error" };
    }
  };

  const updateUser = useCallback(async (userId, updates) => {
    // Self-update goes direct via anon client (RLS users_update_self covers it).
    // Cross-user updates go through /api/admin/users.
    const isSelf = currentUser?.id === userId;

    if (isSelf) {
      const dbUpdates = {};
      if (updates.firstName !== undefined) dbUpdates.first_name = updates.firstName;
      if (updates.lastName !== undefined) dbUpdates.last_name = updates.lastName;
      if (updates.quota !== undefined) dbUpdates.quota = updates.quota;
      if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
      // Note: role/permissions are NOT settable via self-update (those go through admin endpoint)
      if (Object.keys(dbUpdates).length > 0) {
        supabase.from("users").update(dbUpdates).eq("id", userId).then(({ error }) => {
          if (error) console.warn("[CoreFlow Auth] self updateUser failed:", error.message);
        });
      }
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...updates } : u));
      setCurrentUser(prev => ({ ...prev, ...updates }));
      return { success: true };
    }

    const fields = {};
    if (updates.firstName !== undefined) fields.first_name = updates.firstName;
    if (updates.lastName !== undefined) fields.last_name = updates.lastName;
    if (updates.quota !== undefined) fields.quota = updates.quota;
    if (updates.phone !== undefined) fields.phone = updates.phone;
    // role/permissions intentionally excluded — use changeRole / setUserPermissions instead.
    const result = await callAdminUsers({ action: "update", userId, fields });
    if (!result.success) {
      console.error("[CoreFlow Auth] updateUser failed:", result.error);
      return result;
    }
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...updates } : u));
    return { success: true };
  }, [currentUser]);

  const removeUser = useCallback(async (userId) => {
    if (userId === currentUser?.id) return { success: false, error: "Cannot remove yourself" };
    const backup = users.find(u => u.id === userId);
    setUsers(prev => prev.filter(u => u.id !== userId));
    const result = await callAdminUsers({ action: "remove", userId });
    if (!result.success) {
      // rollback
      if (backup) setUsers(prev => [backup, ...prev]);
      console.error("[CoreFlow Auth] removeUser failed:", result.error);
    }
    return result;
  }, [currentUser, users]);

  const suspendUser = useCallback(async (userId) => {
    if (userId === currentUser?.id) return { success: false, error: "Cannot suspend yourself" };
    const result = await callAdminUsers({ action: "suspend", userId });
    if (!result.success) {
      console.error("[CoreFlow Auth] suspendUser failed:", result.error);
      return result;
    }
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: "suspended" } : u));
    return { success: true };
  }, [currentUser]);

  const activateUser = useCallback(async (userId) => {
    const result = await callAdminUsers({ action: "activate", userId });
    if (!result.success) {
      console.error("[CoreFlow Auth] activateUser failed:", result.error);
      return result;
    }
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: "active" } : u));
    return { success: true };
  }, []);

  const changeRole = useCallback(async (userId, newRole) => {
    if (!ROLES[newRole]) return { success: false, error: "Invalid role" };
    // Endpoint enforces last-admin guard server-side; this client check is
    // a fast-path UX hint that catches obvious cases before the round-trip.
    const user = users.find(u => u.id === userId);
    if (user?.role === "admin") {
      const adminCount = users.filter(u => u.role === "admin" && u.status === "active").length;
      if (adminCount <= 1 && newRole !== "admin") return { success: false, error: "Must have at least one admin" };
    }
    const result = await callAdminUsers({ action: "changeRole", userId, role: newRole });
    if (!result.success) {
      console.error("[CoreFlow Auth] changeRole failed:", result.error);
      return result;
    }
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole, permissions: null } : u));
    if (currentUser?.id === userId) setCurrentUser(prev => ({ ...prev, role: newRole, permissions: null }));
    return { success: true };
  }, [users, currentUser]);

  const setUserPermissions = useCallback(async (userId, perms) => {
    const result = await callAdminUsers({ action: "setPermissions", userId, permissions: perms });
    if (!result.success) {
      console.error("[CoreFlow Auth] setUserPermissions failed:", result.error);
      return result;
    }
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, permissions: perms } : u));
    if (currentUser?.id === userId) setCurrentUser(prev => ({ ...prev, permissions: perms }));
    return { success: true };
  }, [currentUser]);

  // ═══════════════════════════════════════════
  // PERMISSION CHECKS
  // ═══════════════════════════════════════════
  const hasPermission = useCallback((permKey) => {
    if (!currentUser) return false;
    if (currentUser.role === "admin") return true;
    const perms = Array.isArray(currentUser.permissions) ? currentUser.permissions : ROLE_PERMISSIONS[currentUser.role] || [];
    return perms.includes(permKey);
  }, [currentUser]);

  const hasAnyPermission = useCallback((...permKeys) => permKeys.some(k => hasPermission(k)), [hasPermission]);
  const hasAllPermissions = useCallback((...permKeys) => permKeys.every(k => hasPermission(k)), [hasPermission]);
  const getUserPermissions = useCallback((userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return [];
    if (user.role === "admin") return Object.keys(PERMISSIONS);
    return user.permissions || ROLE_PERMISSIONS[user.role] || [];
  }, [users]);

  const resetAuth = useCallback(async () => {
    await supabase.auth.signOut();
    setCurrentUser(null); setSession(null); setUsers([]);
    setNeedsOnboarding(false);
  }, []);

  const isAuthenticated = !!currentUser && !!session;

  const value = {
    currentUser, users, isAuthenticated, session, authLoading, authError,
    needsOnboarding, bootstrapFailed, onboardingStep, pendingInvite, isPasswordRecovery,
    login, signup, loginWithGoogle, loginWithMagicLink, logout, resetPassword, resetAuth,
    createOrg, completeOnboarding, updateOnboardingStep,
    inviteUser, updateUser, removeUser, suspendUser, activateUser, changeRole, setUserPermissions, getUserPermissions,
    hasPermission, hasAnyPermission, hasAllPermissions,
    ROLES, PERMISSIONS, ROLE_PERMISSIONS,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
